// The collapsed menu's peek: the rail widens to the full menu over the page,
// which keeps its width, and narrows again once it is left. `side-menu-peek`
// on <html> is the whole state and side-menu.css draws it; the rail toggle
// (side_menu_controller) is what makes the wide menu stay and the page give
// way.
//
// It opens from the pointer resting on the rail, from keyboard focus, and
// from a press — a touch has no hover, and a row that opens in place (a
// group, the account menu) needs the width to open into. It stays while the
// pointer is over the menu or one of its popovers, while keyboard focus is in
// it, and while one of its dropdowns is open.
const CLASS = 'side-menu-peek'
// Long enough that a pointer crossing the rail on its way elsewhere opens
// nothing, and that one overshooting the menu's edge can come back.
const OPEN_DELAY = 100
const CLOSE_DELAY = 250

const GROUP_SUMMARY = 'li.collapse > details > summary'
// The rail's own toggles act on the rail: pressing or focusing one is not a
// reason to peek. Collapsing hands focus to the logo, and the announcement's
// rail tile is not there once the menu is wide.
const RAIL_TOGGLE = '[data-action*="side-menu#toggleRail"]'

export class Peek {
  private menu: HTMLElement | null = null
  private opening: number | undefined
  private closing: number | undefined
  private listeners: Array<() => void> = []
  private pressed = false
  private keyboard = false

  get showing(): boolean {
    return document.documentElement.classList.contains(CLASS)
  }

  // Idempotent, so it can be re-run on every rail change and breakpoint
  // crossing. Only the rail peeks.
  attach(menu: HTMLElement | null, railed: boolean) {
    this.detach()
    if (!menu || !railed) {
      this.hide()
      return
    }

    this.menu = menu
    this.listen('pointermove', (event) => this.onMove(event as PointerEvent))
    this.listen('pointerleave', (event) => this.onLeave(event as PointerEvent))
    this.listen('pointerdown', (event) => this.onPress(event as PointerEvent))
    this.listen('click', (event) => this.onClick(event))
    this.listen('focusin', (event) => this.onFocusIn(event))
    this.listen('focusout', () => this.releaseLater())

    // Turbo Drive keeps <html>, so a peek that was open when a link in it was
    // followed arrives open, with nothing yet to vouch for it.
    this.releaseLater()
  }

  detach() {
    this.settle()
    this.listeners.forEach((remove) => remove())
    this.listeners = []
    this.menu = null
  }

  hide() {
    this.settle()
    this.pressed = false
    this.keyboard = false
    document.documentElement.classList.remove(CLASS)
  }

  private show() {
    this.settle()
    document.documentElement.classList.add(CLASS)
  }

  private settle() {
    window.clearTimeout(this.opening)
    window.clearTimeout(this.closing)
    this.opening = this.closing = undefined
  }

  // Movement, not pointerenter: collapsing shrinks the menu around a pointer
  // that never left it, and that pointer should open the rail the moment it
  // stirs, while one the menu has shrunk away from should not. `:hover` at
  // the end of the delay tells the two apart.
  private onMove(event: PointerEvent) {
    if (isTouch(event)) return
    window.clearTimeout(this.closing)
    if (this.showing || this.opening !== undefined) return

    this.opening = window.setTimeout(() => {
      this.opening = undefined
      if (this.menu?.matches(':hover')) this.show()
    }, OPEN_DELAY)
  }

  private onLeave(event: PointerEvent) {
    if (isTouch(event)) return
    window.clearTimeout(this.opening)
    this.opening = undefined
    this.releaseLater()
  }

  // A press opens at once. A tap on a link is on its way to another page, so
  // only a row that opens in place widens the menu for a touch.
  private onPress(event: PointerEvent) {
    this.pressed = false
    if (this.showing) return
    const target = event.target as Element
    if (target.closest(RAIL_TOGGLE)) return
    if (isTouch(event) && !target.closest(`${GROUP_SUMMARY}, .dropdown`)) return

    this.pressed = true
    this.show()
  }

  // The press that opened the peek on a group is asking for the group: its
  // click must open it, not toggle shut one that was open under the rail.
  private onClick(event: Event) {
    if (!this.pressed) return
    this.pressed = false

    const summary = (event.target as Element).closest(GROUP_SUMMARY)
    if (!summary) return
    event.preventDefault()
    ;(summary.parentElement as HTMLDetailsElement).open = true
  }

  private onFocusIn(event: Event) {
    const target = event.target as Element
    if (!target.matches(':focus-visible') || target.closest(RAIL_TOGGLE)) return

    this.keyboard = true
    this.show()
  }

  private releaseLater() {
    if (!this.showing) return
    window.clearTimeout(this.closing)
    this.closing = window.setTimeout(() => {
      if (!this.held()) this.hide()
    }, CLOSE_DELAY)
  }

  // A click focuses the row it lands on, which is not a reason to stay. An
  // open dropdown is (DaisyUI shows it on focus-within), and so is the
  // keyboard once it has been used in the menu: a peek the pointer opened
  // must not hang on the focus ring that collapsing left on the logo.
  private held(): boolean {
    const menu = this.menu
    if (!menu) return false
    if (canHover() && menu.matches(':hover')) return true

    const active = document.activeElement
    if (!active || !menu.contains(active)) return false
    return active.closest('.dropdown') !== null || (this.keyboard && active.matches(':focus-visible'))
  }

  private listen(type: string, handler: (event: Event) => void) {
    const menu = this.menu!
    menu.addEventListener(type, handler)
    this.listeners.push(() => menu.removeEventListener(type, handler))
  }
}

const isTouch = (event: PointerEvent): boolean => event.pointerType === 'touch'

// A touch leaves `:hover` on whatever was tapped last.
const canHover = (): boolean => window.matchMedia?.('(hover: hover)').matches ?? false
